// frontend/src/components/layout/Footer.jsx
import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Scale,
  Shield,
  Lock,
  FileText,
  Mail,
  ChevronUp,
  BookOpen,
  Cookie,
  RefreshCw,
  Users,
  Megaphone,
  MessageCircle,
  Plus,
  Minus,
  Heart,
  Facebook,
  Instagram,
} from "lucide-react";

// Componente memoizado para cada link (evita re-renders innecesarios)
const FooterLink = memo(({ icon: Icon, name, path, isExternal = false }) => (
  <a
    href={path}
    target={isExternal ? "_blank" : "_self"}
    rel={isExternal ? "noopener noreferrer" : ""}
    className="group flex items-center gap-2 text-xs text-gray-500 hover:text-[#449779] transition-all duration-200 py-1.5 rounded-lg hover:bg-gray-100 px-2 -mx-2"
  >
    <Icon className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#449779] transition-colors" />
    <span>{name}</span>
  </a>
));

FooterLink.displayName = "FooterLink";

// Componente para la sección de documentos
const LegalDocumentsSection = memo(() => {
  const [showAll, setShowAll] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const mainDocuments = [
    { name: "Termos de Uso", icon: FileText, path: "/termos" },
    { name: "Política de Privacidade", icon: Lock, path: "/privacidade" },
  ];

  const secondaryDocuments = [
    { name: "Política da Comunidade", icon: Users, path: "/comunidade" },
    { name: "Política de Publicidade", icon: Megaphone, path: "/publicidade" },
    { name: "Política de Cookies", icon: Cookie, path: "/cookies" },
    { name: "Política de Reembolso", icon: RefreshCw, path: "/reembolso" },
    { name: "Política de Chat", icon: MessageCircle, path: "/politica-chat" },
    {
      name: "Política de Validação de Moradores",
      icon: Shield,
      path: "/validacao",
    },
  ];

  const handleToggle = useCallback(() => {
    setShowAll((prev) => !prev);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-[#449779]" />
        <h3 className="text-sm font-semibold text-gray-700">
          Documentos Legais
        </h3>
      </div>

      <div className="space-y-1">
        {mainDocuments.map((doc, index) => (
          <FooterLink key={index} {...doc} />
        ))}
      </div>

      <button
        onClick={handleToggle}
        aria-expanded={showAll}
        aria-label={
          showAll ? "Mostrar menos documentos" : "Mostrar mais documentos"
        }
        className="flex items-center gap-2 text-xs text-[#449779] hover:text-[#2d5f4d] transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#449779] focus:ring-offset-2 rounded-lg px-2 py-1 -ml-2"
      >
        {showAll ? (
          <>
            <Minus className="w-3.5 h-3.5" />
            <span>Ver menos documentos</span>
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" />
            <span>Ver mais documentos ({secondaryDocuments.length})</span>
          </>
        )}
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          prefersReducedMotion ? "duration-0" : ""
        } ${showAll ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="space-y-1 mt-2 pl-2 border-l-2 border-[#449779]/20">
          {secondaryDocuments.map((doc, index) => (
            <FooterLink key={index} {...doc} />
          ))}
        </div>
      </div>
    </div>
  );
});

LegalDocumentsSection.displayName = "LegalDocumentsSection";

// Componente principal
export default function Footer() {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const quickLinks = [
    { name: "Sobre o Brechó Vizinho", path: "/sobre" },
    { name: "Regras da Comunidade", path: "/comunidade" },
    { name: "Perguntas Frequentes", path: "/faq" },
    { name: "Contato", path: "/contato" },
  ];

  const socialLinks = [
    {
      name: "Instagram",
      icon: Instagram,
      path: "https://instagram.com/brechovizinho",
      isExternal: true,
    },
    {
      name: "Facebook",
      icon: Facebook,
      path: "https://facebook.com/brechovizinho",
      isExternal: true,
    },
  ];

  return (
    <footer
      className="bg-gray-50 border-t border-gray-200 mt-auto"
      role="contentinfo"
    >
      {/* ==================== INFO IMPORTANTE - 2 LÍNEAS ==================== */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-[#449779]/5 to-transparent">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-start gap-3">
            <Scale className="w-5 h-5 text-[#449779] flex-shrink-0 mt-0.5" />
            <div>
              {/* 🔥 PRIMERA LÍNEA: TÍTULO */}
              <p className="text-sm font-semibold text-[#449779]">
                Informação Importante:
              </p>
              {/* 🔥 SEGUNDA LÍNEA: TEXTO COMPLETO */}
              <p className="text-xs text-gray-600 leading-relaxed mt-0.5">
                O <span className="font-semibold">Brechó Vizinho</span> é uma
                plataforma comunitária de divulgação de anúncios entre
                moradores.{" "}
                <span className="font-semibold">
                  Não participamos das negociações, pagamentos, entregas ou
                  garantias relacionadas aos itens anunciados.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== CONTENIDO PRINCIPAL DEL FOOTER ==================== */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Columna 1: Documentos Legales */}
          <LegalDocumentsSection />

          {/* Columna 2: Links Rápidos */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#449779]" />
              Links Rápidos
            </h3>
            <div className="space-y-1">
              {quickLinks.map((link, index) => (
                <FooterLink key={index} icon={FileText} {...link} />
              ))}
            </div>
          </div>

          {/* Columna 3: Contato LGPD */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#449779]" />
              Contato LGPD
            </h3>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-2">
                Para questões sobre seus dados pessoais:
              </p>
              <a
                href="mailto:info@brechovizinho.com.br"
                className="text-xs text-[#449779] font-medium hover:underline block text-center break-all"
              >
                privacidade@brechovizinho.com.br
              </a>
            </div>

            <div className="flex justify-center">
              <div className="bg-green-50 rounded-full px-3 py-1.5 inline-flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-green-600" />
                <span className="text-[10px] text-green-700 font-medium">
                  LGPD Compliance
                </span>
              </div>
            </div>
          </div>

          {/* Columna 4: Redes Sociales y Comunidad */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#449779]" />
              Comunidade
            </h3>

            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                Acompanhe novidades e participe da nossa comunidade
              </p>

              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#449779] transition-colors"
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Selo de seguridad adicional */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-center gap-2">
                <div className="w-6 h-6 bg-[#449779]/10 rounded-full flex items-center justify-center">
                  <Lock className="w-3 h-3 text-[#449779]" />
                </div>
                <span className="text-[9px] text-gray-400">
                  Dados protegidos com criptografia
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-6 pb-10 border-t border-gray-200 text-center">
          <p className="text-[11px] text-gray-400">
            © {currentYear} Brechó Vizinho - Todos os direitos reservados
          </p>
          <p className="text-[10px] text-gray-400 mt-1">
            Versão 1.0 | Comunidade entre vizinhos
          </p>
        </div>
      </div>

      {/* Botón flotante "Volver arriba" mejorado */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-4 bg-[#449779] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-all duration-300 z-40 md:hidden focus:outline-none focus:ring-2 focus:ring-[#449779] focus:ring-offset-2 ${
          showBackToTop
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
        }`}
        aria-label="Voltar ao topo"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    </footer>
  );
}
